import React, { useState } from 'react';
import useCrmStore from '../../../services/useCrmStore';
import { toast } from 'react-toastify';
import '../CrmModals.css';
import {
  FiUser,
  FiMail,
  FiPhone,
  FiBriefcase,
  FiDollarSign,
  FiCalendar,
  FiTarget,
  FiAlertCircle,
  FiActivity,
  FiFileText,
  FiTag,
  FiX,
  FiPlus,
  FiSave
} from 'react-icons/fi';

const CreateLeadForm = ({ stages, defaultStageId, onClose }) => {
  const { createLead } = useCrmStore();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    stage_id: defaultStageId || stages[0]?.id || '',
    source: 'MANUAL',
    deal_value: '',
    expected_close_date: '',
    lead_score: 50,
    priority: 'MEDIUM',
    status: 'ACTIVE',
    description: '',
    tags: []
  });
  const [tagInput, setTagInput] = useState('');
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Nome é obrigatório';
    }

    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }

    if (!formData.stage_id) {
      newErrors.stage_id = 'Estágio é obrigatório';
    }

    if (formData.deal_value && isNaN(parseFloat(formData.deal_value))) {
      newErrors.deal_value = 'Valor inválido';
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        deal_value: formData.deal_value ? parseFloat(formData.deal_value) : null,
        lead_score: parseInt(formData.lead_score),
        tags: formData.tags.length > 0 ? formData.tags : null
      };

      await createLead(payload);
      toast.success('Lead criado com sucesso!');
      onClose();
    } catch (error) {
      toast.error('Falha ao criar lead');
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleAddTag = () => {
    const tag = tagInput.trim();
    if (tag && !formData.tags.includes(tag)) {
      setFormData(prev => ({ ...prev, tags: [...prev.tags, tag] }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleTagInputKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      LOW: '#22c55e',
      MEDIUM: '#f59e0b',
      HIGH: '#ef4444',
      URGENT: '#dc2626'
    };
    return colors[priority] || colors.MEDIUM;
  };

  return (
    <div className="crm-modal-overlay" onClick={onClose}>
      <div className="crm-modal crm-modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="crm-modal-header">
          <div className="crm-modal-header-content">
            <h2 className="crm-modal-title">Criar Novo Lead</h2>
            <p className="crm-modal-subtitle">
              Preencha as informações para adicionar um novo lead ao pipeline
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="crm-modal-close"
            aria-label="Fechar"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="crm-modal-body">
            <h3 className="crm-section-title">
              Informações Básicas
            </h3>

            <div className="crm-form-grid">
              <div className="crm-form-field">
                <label className="crm-form-label">
                  <FiUser className="crm-form-label-icon" />
                  Nome <span className="crm-form-label-required">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`crm-input ${errors.name ? 'crm-input-error' : ''}`}
                  placeholder="Nome completo do lead"
                />
                {errors.name && (
                  <span className="crm-error-message">
                    <FiAlertCircle /> {errors.name}
                  </span>
                )}
              </div>

              <div className="crm-form-field">
                <label className="crm-form-label">
                  <FiMail className="crm-form-label-icon" />
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`crm-input ${errors.email ? 'crm-input-error' : ''}`}
                  placeholder="email@example.com"
                />
                {errors.email && (
                  <span className="crm-error-message">
                    <FiAlertCircle /> {errors.email}
                  </span>
                )}
              </div>

              <div className="crm-form-field">
                <label className="crm-form-label">
                  <FiPhone className="crm-form-label-icon" />
                  Telefone
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="crm-input"
                  placeholder="(00) 00000-0000"
                />
              </div>

              <div className="crm-form-field">
                <label className="crm-form-label">
                  <FiBriefcase className="crm-form-label-icon" />
                  Empresa
                </label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="crm-input"
                  placeholder="Nome da empresa"
                />
              </div>
            </div>

            <h3 className="crm-section-title crm-mt-8">
              Pipeline & Status
            </h3>

            <div className="crm-form-grid">
              <div className="crm-form-field">
                <label className="crm-form-label">
                  <FiActivity className="crm-form-label-icon" />
                  Estágio <span className="crm-form-label-required">*</span>
                </label>
                <select
                  name="stage_id"
                  value={formData.stage_id}
                  onChange={handleChange}
                  className={`crm-select ${errors.stage_id ? 'crm-input-error' : ''}`}
                >
                  <option value="">Selecione um estágio...</option>
                  {stages.map(stage => (
                    <option key={stage.id} value={stage.id}>
                      {stage.name}
                    </option>
                  ))}
                </select>
                {errors.stage_id && (
                  <span className="crm-error-message">
                    <FiAlertCircle /> {errors.stage_id}
                  </span>
                )}
              </div>

              <div className="crm-form-field">
                <label className="crm-form-label">
                  <FiTag className="crm-form-label-icon" />
                  Origem
                </label>
                <select
                  name="source"
                  value={formData.source}
                  onChange={handleChange}
                  className="crm-select"
                >
                  <option value="MANUAL">Manual</option>
                  <option value="FORM">Formulário</option>
                  <option value="WHATSAPP">WhatsApp</option>
                  <option value="INSTAGRAM">Instagram</option>
                  <option value="WEBHOOK">Webhook</option>
                  <option value="API">API</option>
                </select>
              </div>

              <div className="crm-form-field">
                <label className="crm-form-label">
                  Prioridade
                </label>
                <select
                  name="priority"
                  value={formData.priority}
                  onChange={handleChange}
                  className="crm-select"
                  style={{
                    borderColor: getPriorityColor(formData.priority)
                  }}
                >
                  <option value="LOW">Baixa</option>
                  <option value="MEDIUM">Média</option>
                  <option value="HIGH">Alta</option>
                  <option value="URGENT">Urgente</option>
                </select>
              </div>

              <div className="crm-form-field">
                <label className="crm-form-label">
                  Status
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="crm-select"
                >
                  <option value="ACTIVE">Ativo</option>
                  <option value="INACTIVE">Inativo</option>
                  <option value="BLOCKED">Bloqueado</option>
                </select>
              </div>
            </div>

            <h3 className="crm-section-title crm-mt-8">
              Informações do Negócio
            </h3>

            <div className="crm-form-grid">
              <div className="crm-form-field">
                <label className="crm-form-label">
                  <FiDollarSign className="crm-form-label-icon" />
                  Valor do Negócio
                </label>
                <input
                  type="number"
                  name="deal_value"
                  value={formData.deal_value}
                  onChange={handleChange}
                  className={`crm-input ${errors.deal_value ? 'crm-input-error' : ''}`}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                />
                <span className="crm-form-hint">Valor estimado da oportunidade em R$</span>
                {errors.deal_value && (
                  <span className="crm-error-message">
                    <FiAlertCircle /> {errors.deal_value}
                  </span>
                )}
              </div>

              <div className="crm-form-field">
                <label className="crm-form-label">
                  <FiCalendar className="crm-form-label-icon" />
                  Data Prevista de Fechamento
                </label>
                <input
                  type="date"
                  name="expected_close_date"
                  value={formData.expected_close_date}
                  onChange={handleChange}
                  className="crm-input"
                />
                <span className="crm-form-hint">Quando espera fechar este negócio?</span>
              </div>

              <div className="crm-form-field crm-form-grid-full">
                <label className="crm-form-label">
                  <FiTarget className="crm-form-label-icon" />
                  Pontuação do Lead: {formData.lead_score}
                </label>
                <input
                  type="range"
                  name="lead_score"
                  value={formData.lead_score}
                  onChange={handleChange}
                  min="0"
                  max="100"
                  className="crm-input"
                />
                <span className="crm-form-hint">
                  Qualificação do lead de 0 a 100 (quanto maior, melhor qualificado)
                </span>
              </div>
            </div>

            <div className="crm-form-field crm-mt-4">
              <label className="crm-form-label">
                <FiFileText className="crm-form-label-icon" />
                Descrição
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                className="crm-textarea"
                placeholder="Adicione informações adicionais sobre este lead..."
                rows="4"
              />
              <span className="crm-form-hint">
                Detalhes, observações ou contexto adicional sobre o lead
              </span>
            </div>

            <div className="crm-form-field crm-mt-4">
              <label className="crm-form-label">
                <FiTag className="crm-form-label-icon" />
                Tags
              </label>
              <div className="crm-tag-input-wrapper">
                <div className="crm-tag-input-container">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={handleTagInputKeyPress}
                    className="crm-input crm-tag-input"
                    placeholder="Digite uma tag e pressione Enter..."
                  />
                  <button
                    type="button"
                    onClick={handleAddTag}
                    className="crm-btn-icon"
                    disabled={!tagInput.trim()}
                  >
                    <FiPlus />
                  </button>
                </div>

                {formData.tags.length > 0 && (
                  <div className="crm-tag-list">
                    {formData.tags.map(tag => (
                      <div key={tag} className="crm-tag">
                        {tag}
                        <button
                          type="button"
                          onClick={() => handleRemoveTag(tag)}
                          className="crm-tag-remove"
                        >
                          <FiX size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <span className="crm-form-hint">
                Use tags para categorizar e filtrar leads facilmente
              </span>
            </div>
          </div>

          <div className="crm-modal-footer">
            <button
              type="button"
              onClick={onClose}
              className="crm-btn crm-btn-secondary"
              disabled={submitting}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="crm-btn crm-btn-primary"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <span className="crm-spinner" />
                  Criando...
                </>
              ) : (
                <>
                  <FiSave />
                  Criar Lead
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateLeadForm;
